import { useState, useEffect, useCallback } from 'react';
import { getProjectPhotos } from '../services/photoService';
import type { ProjectPhotoView } from '../types';

interface UseProjectPhotosOptions {
  projectId: string | null;
  autoFetch?: boolean;
}

interface UseProjectPhotosReturn {
  photos: ProjectPhotoView[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useProjectPhotos({
  projectId,
  autoFetch = true,
}: UseProjectPhotosOptions): UseProjectPhotosReturn {
  const [photos, setPhotos] = useState<ProjectPhotoView[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPhotos = useCallback(async () => {
    if (!projectId) {
      setPhotos([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getProjectPhotos(projectId);
      setPhotos(data);
    } catch (err) {
      console.error('Error fetching project photos:', err);
      setError(err instanceof Error ? err.message : 'Failed to load photos');
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (autoFetch) {
      fetchPhotos();
    }
  }, [autoFetch, fetchPhotos]);

  return {
    photos,
    isLoading,
    error,
    refetch: fetchPhotos,
  };
}